import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Switch,
    Alert,
    Avatar
} from '@mui/material';
import { useAuth } from '../auth';
import { AdminEpisode, blacklistedEpisodeService } from '../service/BlacklistedEpisodeService';
import { format } from 'date-fns';

const ManageBlacklistedEpisodes: React.FC = () => {
    const { IsAdmin, cookies } = useContext(useAuth);
    const [episodes, setEpisodes] = useState<AdminEpisode[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [updatingId, setUpdatingId] = useState<string | null>(null);

    useEffect(() => {
        if (IsAdmin && cookies?.UserAuth?.token) {
            fetchEpisodes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [IsAdmin, cookies]);

    const fetchEpisodes = async () => {
        try {
            setLoading(true);
            setError('');
            const token = cookies?.UserAuth?.token;
            if (token) {
                const data = await blacklistedEpisodeService.getEpisodes(token);
                setEpisodes(data);
            } else {
                setError('Authentication token not found');
            }
        } catch (err) {
            setError('Failed to fetch episodes');
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async (episode: AdminEpisode) => {
        if (!cookies?.UserAuth?.token) return;
        const nextBlacklisted = !episode.isBlacklisted;

        try {
            setUpdatingId(episode.id);
            await blacklistedEpisodeService.setBlacklisted(episode.id, nextBlacklisted, episode.name, cookies.UserAuth.token);
            setEpisodes(prev => prev.map(e => e.id === episode.id ? { ...e, isBlacklisted: nextBlacklisted } : e));
            setSuccess(nextBlacklisted
                ? `"${episode.name}" is now hidden from the streaming list`
                : `"${episode.name}" is visible in the streaming list again`);
        } catch (err) {
            setError(`Failed to update "${episode.name}"`);
        } finally {
            setUpdatingId(null);
        }
    };

    if (!IsAdmin) {
        return <Typography variant="h5" color="error" align="center" sx={{ mt: 4 }}>Access Denied</Typography>;
    }

    return (
        <Box sx={{ p: 3, pt: 10 }}>
            <Typography variant="h4" gutterBottom>Manage Episodes</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Toggle an episode off to remove it from the streaming list for all users. This does not delete anything from Boxcast.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Airs / Aired</TableCell>
                            <TableCell align="right">Visible in Streaming List</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {episodes.map((episode) => (
                            <TableRow key={episode.id} sx={episode.isBlacklisted ? { opacity: 0.5 } : undefined}>
                                <TableCell>
                                    <Avatar variant="rounded" src={episode.poster} alt={episode.name} />
                                </TableCell>
                                <TableCell>{episode.name}</TableCell>
                                <TableCell>
                                    {episode.startsAt ? format(new Date(episode.startsAt), 'MM/dd/yyyy h:mm a') : '—'}
                                </TableCell>
                                <TableCell align="right">
                                    <Switch
                                        checked={!episode.isBlacklisted}
                                        disabled={updatingId === episode.id}
                                        onChange={() => handleToggle(episode)}
                                        inputProps={{ 'aria-label': `Toggle visibility for ${episode.name}` }}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                        {!loading && episodes.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">No episodes found</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default ManageBlacklistedEpisodes;
